import { useCallback, type RefObject } from 'react';

import type { SpinButtonTextSelection } from '../types.js';

/**
 * This hook provides text selection and cursor utility functions.
 * Because the SpinButton is a contenteditable span, this is not provided natively.
 */
export const useSelection = (
  ref: RefObject<HTMLElement | null> | undefined,
) => {
  /** Clear current text selection */
  const deselectAll = useCallback(() => {
    window.getSelection()?.removeAllRanges();
  }, []);

  /** Select the whole text content of the ref element. */
  const selectAll = useCallback(() => {
    if (!ref?.current) {
      return;
    }

    const el = ref.current;
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const newRange = document.createRange();

    if (!el.childNodes.length) {
      // No content to select; place a cursor at position 0 so the spinbutton's
      // keydown handler receives a valid selection and can accept typed input.
      newRange.setStart(el, 0);
      newRange.collapse(true);
    } else {
      newRange.selectNodeContents(el);
    }

    selection.removeAllRanges();
    selection.addRange(newRange);
  }, [ref]);

  /** Returns the current text selection of the spin button. */
  const getSelection = useCallback((): SpinButtonTextSelection | undefined => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      return undefined;
    }

    const range = selection.getRangeAt(0);
    const el = ref?.current;

    // When the selection was created via selectNodeContents (e.g. selectAll),
    // the range containers are the element itself and offsets are child-node
    // indices instead of character positions. Convert them to character offsets.
    if (el && (range.startContainer === el || range.endContainer === el)) {
      const toCharOffset = (container: Node, offset: number): number => {
        if (container !== el) {
          return offset;
        }
        let chars = 0;
        for (let i = 0; i < offset; i++) {
          chars += el.childNodes[i]?.textContent?.length ?? 0;
        }
        return chars;
      };
      return {
        start: toCharOffset(range.startContainer, range.startOffset),
        end: toCharOffset(range.endContainer, range.endOffset),
        rtl: false,
      };
    }

    // return undefined if the selected range is outside of the spinbutton
    if (
      range.startContainer !== range.endContainer &&
      range.startContainer !== ref?.current
    ) {
      return undefined;
    }

    return { start: range.startOffset, end: range.endOffset, rtl: false };
    // Ignore ref dependency
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Set the text selection within the spin button */
  const setSelection = useCallback(
    (selection: Partial<SpinButtonTextSelection>) => {
      const textNode = ref?.current?.childNodes[0];
      if (!textNode) {
        return;
      }

      const length = textNode.textContent?.length ?? 0;
      const { rtl = false } = selection;
      let { start = 0, end = length } = selection;

      if (rtl) {
        start = length - start;
        end = length - end;
      }

      // fix boundaries
      start = Math.min(length, start);
      end = Math.min(length, end);

      // create new selection and overwrite an existing one
      const newRange = document.createRange();
      newRange.setStart(textNode, start);
      newRange.setEnd(textNode, end);

      deselectAll();
      window.getSelection()?.addRange(newRange);
    },
    [deselectAll, ref],
  );

  return {
    selectAll,
    deselectAll,
    getSelection,
    setSelection,
  };
};
